import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Phone, Clock } from 'lucide-react';

// Compact icon components
const OTIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><rect x="5" y="8" width="22" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M16 12V20M12 16H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
);
const ICUIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><rect x="4" y="10" width="24" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M8 16H12L14 12L16 20L18 14L20 16H24" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
);
const AmbIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><rect x="3" y="10" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M21 14H26C27 14 28 15 28 16V22H21V14Z" stroke="currentColor" strokeWidth="1.5"/><circle cx="9" cy="23" r="2" stroke="currentColor" strokeWidth="1.5"/><circle cx="24" cy="23" r="2" stroke="currentColor" strokeWidth="1.5"/></svg>
);
const NICUIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><circle cx="16" cy="12" r="5" stroke="currentColor" strokeWidth="1.5"/><path d="M10 28V24C10 21 13 18 16 18C19 18 22 21 22 24V28" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
);
const DialIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><ellipse cx="16" cy="16" rx="8" ry="10" stroke="currentColor" strokeWidth="1.5"/><path d="M12 10C13 13 13 19 12 22M20 10C19 13 19 19 20 22" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
);
const LabIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><path d="M12 4V12L7 24C6.5 25 7 26 8 26H24C25 26 25.5 25 25 24L20 12V4" stroke="currentColor" strokeWidth="1.5"/><path d="M12 4H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
);
const PharmIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><rect x="8" y="5" width="16" height="22" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M8 10H24" stroke="currentColor" strokeWidth="1.5"/><path d="M16 14V22M12 18H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
);
const HomeIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><path d="M4 14L16 4L28 14V26C28 27 27 28 26 28H6C5 28 4 27 4 26V14Z" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M12 28V18H20V28" stroke="currentColor" strokeWidth="1.5"/></svg>
);
const InsIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><path d="M16 3L26 8V16C26 22 21 27 16 29C11 27 6 22 6 16V8L16 3Z" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M11 16L14 19L21 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
);
const PhysIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><circle cx="16" cy="8" r="4" stroke="currentColor" strokeWidth="1.5"/><path d="M16 12V20M10 16L16 20L22 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M12 24L16 20L20 24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg>
);

const FacilitiesSection = () => {
  const { t, language } = useLanguage();

  const facilities = [
    { key: 'ot', Icon: OTIcon, color: '#0066b3' },
    { key: 'icu', Icon: ICUIcon, color: '#c41e3a' },
    { key: 'emergency', Icon: AmbIcon, color: '#c41e3a' },
    { key: 'nicu', Icon: NICUIcon, color: '#e91e8c' },
    { key: 'dialysis', Icon: DialIcon, color: '#7b1fa2' },
    { key: 'lab', Icon: LabIcon, color: '#00897b' },
    { key: 'pharmacy', Icon: PharmIcon, color: '#f57c00' },
    { key: 'homecare', Icon: HomeIcon, color: '#0097a7' },
    { key: 'insurance', Icon: InsIcon, color: '#1a8a5c' },
    { key: 'physio', Icon: PhysIcon, color: '#d4a24c' },
  ];

  return (
    <section id="facilities" className="py-16 lg:py-20 bg-[#f8fafc]" data-testid="facilities-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 bg-[#0066b3]/8 text-[#0066b3] rounded-full text-xs font-semibold uppercase tracking-wider mb-3">
            {language === 'ta' ? 'வசதிகள்' : 'Facilities'}
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0f172a] tracking-tight">
            {language === 'ta' ? 'சென்னையின் சிறந்த மருத்துவமனை வசதிகள்' : 'Best Hospital Facilities in Chennai'}
          </h2>
        </div>

        {/* Compact icon grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-10">
          {facilities.map((facility) => {
            const name = t(`facilities.items.${facility.key}.name`);
            return (
              <div
                key={facility.key}
                className="glass rounded-xl p-4 text-center shadow-sm border border-white/50 hover:shadow-md hover:-translate-y-0.5 transition-all"
                data-testid={`facility-card-${facility.key}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2"
                  style={{ backgroundColor: `${facility.color}12`, color: facility.color }}
                >
                  <facility.Icon />
                </div>
                <h3 className="text-xs font-semibold text-[#0f172a] leading-tight">{name}</h3>
              </div>
            );
          })}
        </div>

        {/* Stats row + Emergency CTA */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100">
            <div className="p-5 text-center">
              <div className="text-2xl font-extrabold text-[#0066b3]">3</div>
              <div className="text-xs text-[#64748b]">{language === 'ta' ? 'அறுவை சிகிச்சை அறைகள்' : 'Operation Theatres'}</div>
            </div>
            <div className="p-5 text-center">
              <div className="text-2xl font-extrabold text-[#0066b3]">8</div>
              <div className="text-xs text-[#64748b]">{language === 'ta' ? 'ICU படுக்கைகள்' : 'ICU Beds'}</div>
            </div>
            <div className="p-5 text-center">
              <div className="text-2xl font-extrabold text-[#0066b3]">75+</div>
              <div className="text-xs text-[#64748b]">{language === 'ta' ? 'மொத்த படுக்கைகள்' : 'Total Beds'}</div>
            </div>
            <div className="p-5 text-center bg-[#c41e3a] text-white">
              <div className="text-2xl font-extrabold">24/7</div>
              <div className="text-xs opacity-80">{language === 'ta' ? 'ஆம்புலன்ஸ்' : 'Ambulance'}</div>
            </div>
          </div>
        </div>

        {/* Emergency strip */}
        <div className="mt-6 bg-gradient-to-r from-[#0066b3] to-[#0088cc] rounded-xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-white">
            <div className="w-12 h-12 bg-white/15 rounded-full flex items-center justify-center">
              <Phone size={22} className="animate-pulse" />
            </div>
            <div>
              <h3 className="font-bold text-lg">{language === 'ta' ? '24/7 அவசர சேவைகள்' : '24/7 Emergency Services'}</h3>
              <p className="text-white/70 text-xs flex items-center gap-1"><Clock size={12} /> ICU backup & trauma care</p>
            </div>
          </div>
          <a href="tel:+914428442626" className="bg-white text-[#0066b3] px-6 py-3 rounded-lg font-bold hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm shadow-lg">
            <Phone size={16} /> +91 44 2844 2626
          </a>
        </div>
      </div>
    </section>
  );
};

export default FacilitiesSection;
